/*
By default, Klaro will load the config from a global 'klaroConfig' variable. You
can change this by specifying the 'data-klaro-config' attribute on your script:
<script src="klaro.js" data-klaro-config="myConfigVariableName"
*/
import * as klaro from 'klaro';
import { AdCampaignDataToCookie, getCookie, loadScript } from './components/tools';

const COOKIE_DOMAIN = __FULL_WEBSITE_URL__.split('://')[1].split(':')[0]; // Extracting the domain from the full URL
const COOKIE_NAME = 'cookies_consent';
const COOKIE_POLICY_URL_PL = '/polityka-cookies'; // Hardcoded URL for the cookie policy
const LEARN_MORE_PL = 'Dowiedz się więcej';
const LEARN_MORE_EN = 'Learn more';

function getCookiePolicyElement(text, url) {
  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
}
function getIternalCookiePolicyElement(text) {
  return `<a class="show-popup text-danger" data-target="${COOKIE_POLICY_URL_PL}">${text}</a>`;
}

const klaroConfig = {
  /*
  Setting 'testing' to 'true' will cause Klaro to not show the consent notice or
  modal by default, except if a special hash tag is appended to the URL (#klaro-
  testing). This makes it possible to test Klaro on your live website without
  affecting normal visitors.
  */
  testing: false,

  // Lang is set after this const, so its content can be dynamically set based on the user's language.
  // lang: 'pl',

  /*
  You can customize the title of the consent modal. By default, Klaro will hide the
  modal title, but you can set 'showNoticeTitle' to 'true' to show it.
   */
  showNoticeTitle: true,

  /*
  You can toggle the display of the powered by Klaro link in the consent modal.
   */
  disablePoweredBy: true,

  /*
  You can customize the ID of the DIV element that Klaro will create when starting
  up. By default, Klaro will use 'klaro'.
  */
  elementID: 'klaro',

  /*
  You can customize how Klaro persists consent information in the browser. Specify
  either cookie' (the default) or 'localStorage'.
  */
  storageMethod: 'cookie',

  /*
  You can customize the name of the cookie or localStorage entry that Klaro will
  use for storing the consent information. By default, Klaro will use 'klaro'.
  */
  storageName: COOKIE_NAME,

  /*
  If set to `true`, Klaro will render the texts given in the
  `consentModal.description` and `consentNotice.description` translations as HTML.
  This enables you to e.g. add custom links or interactive content.
  */
  htmlTexts: true,

  /*
  You can change the cookie domain for the consent manager itself. Use this if you
  want to get consent once for multiple matching domains. By default, Klaro will
  use the current domain. Only relevant if 'storageMethod' is set to 'cookie'.
  */
  cookieDomain: COOKIE_DOMAIN,

  /*
  You can also set a custom expiration time for the Klaro cookie. By default, it
  will expire after 30 days. Only relevant if 'storageMethod' is set to 'cookie'.
  */
  cookieExpiresAfterDays: 365,

  /*
  Defines the default state for services in the consent modal (true=enabled by
  default). You can override this setting in each service.
  */
  default: false,

  /*
  If 'mustConsent' is set to 'true', Klaro will directly display the consent
  manager modal and not allow the user to close it before having actively
  consented or declined the use of third-party services.
  */
  mustConsent: false,

  /*
  Setting 'acceptAll' to 'true' will show an "accept all" button in the notice and
  modal, which will enable all third-party services if the user clicks on it. If
  set to 'false', there will be an "accept" button that will only enable the
  services that are enabled in the consent modal.
  */
  acceptAll: true,

  /*
  Setting 'hideDeclineAll' to 'true' will hide the "decline" button in the consent
  modal and force the user to open the modal in order to change his/her consent or
  disable all third-party services. We strongly advise you to not use this
  feature, as it opposes the "privacy by default" and "privacy by design"
  principles of the GDPR (but might be acceptable in other legislations such as
  under the CCPA)
  */
  hideDeclineAll: false,

  /*
  Setting 'hideLearnMore' to 'true' will hide the "learn more / customize" link in
  the consent notice. We strongly advise against using this under most
  circumstances, as it keeps the user from customizing his/her consent choices.
  */
  hideLearnMore: false,

  /*
  You can overwrite existing translations and add translations for your service
  descriptions and purposes. See `src/translations/` for a full list of
  translations that can be overwritten:
  https://github.com/KIProtect/klaro/tree/master/src/translations
  */
  translations: {
    /*
        The `zz` key contains default translations that will be used as fallback values.
            This can e.g. be useful for defining a fallback privacy policy URL.
        */
    en: {
      ok: 'Accept and proceed to the website',
      decline: 'Reject all',
      consentNotice: {
        title: 'We care about your privacy',
        description: `
          <p>We use cookies and similar technologies provided by us and our partners. This involves saving, storing, or accessing information already stored in your web browser.</p>
          <p>We use these technologies to enable you to use the Pomagam.pl platform properly, including securely and efficiently (these are known as essential cookies). ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</p>
          <p>Additionally, we use optional cookies and similar technologies for the purposes listed below, for which you may choose to give or withhold your consent. You can change your choices at any time by visiting the “Manage cookies consent” functionality on Pomagam.pl.<br>We use optional cookies for the following purposes:</p>
          <ol>
            <li><b>Analytics</b> – We use so-called analytical cookies to improve the functioning of the Pomagam.pl platform. Thanks to these cookies, we know how you use our platform, for example, where you come from and how much time you spend on the site. This allows us to create and analyze specific statistics. ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</li>
            <li><b>Retargeting</b> – We and our trusted partners use cookies and similar technologies to present users with specific, including personalized, messages and advertising content, especially related to fundraising campaigns conducted on the site. ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</li>
          </ol>
        `,
        learnMore: 'Customize my selection',
      },
      consentModal: {
        title: 'Adjust settings',
        description: 'You can change or withdraw your Optional Cookie consent at any time.',
      },
      privacyPolicyUrl: COOKIE_POLICY_URL_PL,
      privacyPolicy: {
        name: 'Pomagam.pl privacy policy',
        text: 'More information can be found in the {privacyPolicy}',
      },
      purposes: {
        internal: {
          title: 'Essential cookies',
          description: 'These are necessary for our website to function properly and cannot be disabled. We also use data from these cookies to ensure your security and to detect errors on the site.',
        },
        analytics: {
          title: 'Analytical cookies',
          description: 'Analytical cookies measure visit counts and collect information about traffic sources. We use them to improve the performance of our website. They help us understand how you navigate our site.',
        },
        retargeting: {
          title: 'Marketing cookies',
          description: 'These cookies allow our trusted partners to display more relevant messages tailored to your preferences.',
        },
      },
    },
    pl: {
      ok: 'Akceptuję i przechodzę do serwisu',
      decline: 'Odrzuć wszystkie',
      consentNotice: {
        title: 'Informacje o plikach cookie',
        description: `
          <p>Korzystamy z plików cookie oraz podobnych rozwiązań technologicznych, zarówno własnych, jak i naszych partnerów. Obejmuje to zapisywanie i przechowywanie informacji w pamięci Twojego urządzenia końcowego (takiego jak np. laptop, tablet, smartfon) oraz późniejsze uzyskiwanie do nich dostępu.</p>
          <p>Wykorzystujemy te technologie przede wszystkim po to, aby zapewnić prawidłowe działanie serwisu Pomagam.pl, w tym jego bezpieczeństwo oraz efektywność funkcjonowania. Służą temu tzw. <b>niezbędne pliki cookie</b>, które pozostają zawsze aktywne. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</p>
          <p>Dodatkowo, używamy <b>opcjonalnych plików cookie</b> oraz podobnych technologii do celów analitycznych i retargetingowych. Możesz wyrazić zgodę na ich stosowanie lub jej odmówić. W dowolnym momencie masz możliwość zmiany swoich preferencji na stronie „Zarządzaj zgodami cookies”, do której link znajdziesz w stopce serwisu Pomagam.pl. Opcjonalne pliki cookie wykorzystywane są w następujących celach:</p>
          <ol>
            <li><b>Analityka</b> – używamy tzw. plików cookie analitycznych, aby usprawniać działanie serwisu Pomagam.pl. Dzięki nim możemy zrozumieć, jak użytkownicy poruszają się po naszej stronie – skąd trafiają na portal i jak długo z niego korzystają. Pozwala nam to na tworzenie statystyk oraz ich analizę, co wspiera dalszy rozwój serwisu. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</li>
            <li><b>Retargeting</b> – my oraz nasi zaufani partnerzy stosujemy pliki cookie i inne podobne technologie, aby prezentować użytkownikom określone, w tym spersonalizowane komunikaty i treści reklamowe, zwłaszcza dotyczące prowadzonych w serwisie zbiórek. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</li>
          </ol>
          <p>Ponadto na stronach niektórych zbiórek narzędzia te mogą być uruchamiane także przez organizatora danej zbiórki, na potrzeby jego analityki lub retargetingu.</p>
        `,
        learnMore: 'Dostosuj mój wybór',
      },
      consentModal: {
        title: 'Zarządzaj swoimi preferencjami',
        description: 'Masz możliwość w każdej chwili zmodyfikować lub odwołać udzieloną wcześniej zgodę na stosowanie opcjonalnych plików cookie.',
      },
      privacyPolicyUrl: COOKIE_POLICY_URL_PL,
      privacyPolicy: {
        name: 'Polityce stosowania plików cookies',
        text: 'Szczegółowe informacje znajdziesz w naszej {privacyPolicy}',
      },
      purposes: {
        internal: {
          title: 'Niezbędne pliki cookie',
          description: 'Te pliki są wymagane do prawidłowego funkcjonowania serwisu Pomagam.pl. Dzięki tym plikom możemy także zapewnić bezpieczeństwo korzystania z portalu oraz identyfikować i usuwać błędy techniczne.',
        },
        analytics: {
          title: 'Analityczne pliki cookie',
          description: 'Te pliki służą do zbierania danych statystycznych na temat odwiedzin i źródeł ruchu na stronie Pomagam.pl. Wykorzystujemy je, aby lepiej zrozumieć sposób korzystania z naszego serwisu i wprowadzać usprawnienia w jego działaniu.',        },
        retargeting: {
          title: 'Retargetingowe pliki cookie',
          description: 'Te pliki umożliwiają nam oraz naszym zaufanym partnerom prezentowanie trafniejszych, w tym spersonalizowanych komunikatów i treści reklamowych, dostosowanych do Twoich zainteresowań i aktywności. Komunikaty te dotyczą przede wszystkim prowadzonych w serwisie zbiórek i mogą być wyświetlane zarówno na stronie Pomagam.pl jak i w serwisach zewnętrznych.',
        },
      },
    },
  },

  /*
  Here you specify the third-party services that Klaro will manage for you.
  */
  services: [
    {
      name: 'pomagam',
      title: 'Pomagam.pl',
      translations: {
        en: {
          description: `
            The cookies we use are essential for remembering user preferences (including cookie consent), maintaining sessions (especially for navigation parameters, registration, login, and payment processing), ensuring site security (especially protection against fraud, authentication, and attack prevention), and correct content presentation.
            <table class="cookie-table">
              <thead><tr><td>Name</td><td>Storage period</td><td>Description</td></tr></thead>
              <tbody>
                <tr><td>rcs</td><td>3 minutes</td><td>Used to provide support after a donation</td></tr>
                <tr><td>asi</td><td>Until deleted by the user</td><td>Used to detect suspicious transactions</td></tr>
                <tr><td>aa</td><td>365 days</td><td>Used to detect fraud</td></tr>
                <tr><td>s_nd</td><td>Until deleted by the user</td><td>Shows the organizer a default donation amount</td></tr>
                <tr><td>fbsr_*</td><td>Session</td><td>Used for Facebook login</td></tr>
                <tr><td>fbm_*</td><td>365 days</td><td>Used for Facebook login</td></tr>
                <tr><td>r_prs</td><td>Until deleted by the user</td><td>Used to render the recently viewed campaigns section</td></tr>
                <tr><td>rpc</td><td>365 days</td><td>Used to store the last payment channel</td></tr>
                <tr><td>ltrcs</td><td>7 days</td><td>Used to provide support after a donation</td></tr>
                <tr><td>cookies_consent</td><td>365 days</td><td>Used to remember cookie preferences</td></tr>
              </tbody>
            </table>
            Third-party access: no.<br>
            ${getIternalCookiePolicyElement('Pomagam.pl Cookie Policy')}
          `,
        },
        pl: {
          description: `
            Wykorzystywane przez nas niezbędne pliki cookie są stosowane w celu zapamiętywania preferencji użytkownika (w tym zgody na wykorzystywanie plików cookie), do utrzymania sesji (w szczególności w celu zachowania parametrów nawigacyjnych, obsługi rejestracji, logowania i obsługi procesu płatności), zapewnienia bezpieczeństwa strony (w szczególności ochrony przed oszustwami, uwierzytelniania i zapobiegania atakom) oraz poprawnej prezentacji treści na stronie.
            <table class="cookie-table">
              <thead><tr><td>Nazwa</td><td>Okres przechowywania</td><td>Opis</td></tr></thead>
              <tbody>
                <tr><td>rcs</td><td>3 minuty</td><td>Używane do zapewnienia wsparcia po wpłacie</td></tr>
                <tr><td>asi</td><td>do czasu usunięcia przez użytkownika</td><td>Używane do wykrywania podejrzanych transakcji</td></tr>
                <tr><td>aa</td><td>365 dni</td><td>Używane do wykrywania oszustw</td></tr>
                <tr><td>s_nd</td><td>do czasu usunięcia przez użytkownika</td><td>Pokazuje organizatorowi domyślną kwotę darowizny</td></tr>
                <tr><td>fbsr_*</td><td>Sesja</td><td>Używane do logowania z pomocą Facebooka</td></tr>
                <tr><td>fbm_*</td><td>365 dni</td><td>Używane do logowania z pomocą Facebooka</td></tr>
                <tr><td>r_prs</td><td>do czasu usunięcia przez użytkownika</td><td>Używane do renderowania sekcji ostatnio przeglądanych zbiórek</td></tr>
                <tr><td>rpc</td><td>365 dni</td><td>Używane do przechowywania ostatniego kanału płatności</td></tr>
                <tr><td>ltrcs</td><td>7 dni</td><td>Używane do zapewnienia wsparcia po wpłacie</td></tr>
                <tr><td>cookies_consent</td><td>365 dni</td><td>Używane do zapamiętania preferencji ciastek</td></tr>
              </tbody>
            </table>
            Dostęp podmiotów trzecich: nie.<br>
            ${getIternalCookiePolicyElement('Polityka stosowania plików cookie Pomagam.pl')}
          `,
        },
      },
      required: true,
      default: true,
      /*
      If 'optOut' is set to 'true', Klaro will load this service even before the user
      has given explicit consent. We strongly advise against this.
      */
      optOut: false,
      purposes: ['internal'],
      onInit: `
        // initialization code here (will be executed only once per page-load)
        window.dataLayer = window.dataLayer || [];
        window.gtag = function(){dataLayer.push(arguments)}
        gtag('consent', 'default', {'analytics_storage': 'denied', 'ad_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied'})
        gtag('set', 'ads_data_redaction', true)  // Has no effect once ad_storage is granted, so no need to set it to 'false' on 'google_ads' grant.
      `,
    },
    {
      name: 'cloudflare',
      title: 'Cloudflare',
      translations: {
        en: {
          description: `
            Cloudflare is responsible for ensuring the security and uninterrupted operation of our site. Cookies are used in this case to remember information about successful security checks.<br>
            Third-party access: yes. Storage period: up to 90 days.<br>
            ${getCookiePolicyElement(
              'Cloudflare Privacy Policy',
              'https://www.cloudflare.com/privacypolicy/',
            )}
          `,
        },
        pl: {
          description: `
            Cloudflare odpowiada za zapewnienie bezpieczeństwa oraz nieprzerwanego działania naszej strony. Pliki cookie są w tym przypadku wykorzystywane do zapamiętania informacji o pomyślnym przejściu kontroli bezpieczeństwa.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 90 dni.<br>
            ${getCookiePolicyElement(
              'Polityka prywatności Cloudflare',
              'https://www.cloudflare.com/pl-pl/privacypolicy/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'meta_facebook_login',
      title: 'Meta (Facebook)',
      translations: {
        en: {
          description: `
            Cookies are essential during registration or login to Pomagam.pl using a Facebook account. They are saved during the login process. Meta may use data about your activity on Pomagam.pl to supplement information within other Meta services, e.g., on Facebook. Privacy settings can be managed in ${getCookiePolicyElement('Meta Privacy Center', 'https://www.facebook.com/privacy/center/?locale=pl_PL')}<br>
            Third-party access: yes. Storage period: up to 90 days.<br>
            ${getCookiePolicyElement(
              'Meta Cookie Policy',
              'https://www.facebook.com/privacy/policies/cookies',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są niezbędne podczas rejestracji lub logowania do serwisu Pomagam.pl z użyciem konta Facebook. Są zapisywane w trakcie procesu logowania. Meta może wykorzystywać dane dotyczące Twojej aktywności w serwisie Pomagam.pl w celu uzupełnienia informacji w ramach innych usług Meta, np. na Facebooku. Zarządzanie ustawieniami prywatności możliwe jest w ${getCookiePolicyElement('Centrum ochrony prywatności Meta', 'https://www.facebook.com/privacy/center/?locale=pl_PL')}<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 90 dni.<br>
            ${getCookiePolicyElement(
              'Zasady stosowania plików cookie Meta',
              'https://www.facebook.com/privacy/policies/cookies?locale=pl_PL',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'zen',
      title: 'Zen',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Zen. They are saved during the payment process, for the purpose of its completion.<br>
            Third-party access: yes. Storage period: up to 1 year.<br>
            ${getCookiePolicyElement(
              'ZEN Cookie Policy',
              'https://www.zen.com/files/tc/cookies_policy.pdf',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Zen. Zapisywane są w czasie przechodzenia przez proces płatności, na potrzeby jego realizacji.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do roku.<br>
            ${getCookiePolicyElement(
              'Polityka cookies ZEN',
              'https://www.zen.com/files/tc/cookies_policy.pdf',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'stripe',
      title: 'Stripe',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Stripe. They are saved during the payment process, for the purpose of its completion.<br>
            Third-party access: yes. Storage period: up to 2 years.<br>
            ${getCookiePolicyElement(
              'Stripe Cookie Policy',
              'https://stripe.com/legal/cookies-policy',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Stripe. Zapisywane są w czasie przechodzenia przez proces płatności, na potrzeby jego realizacji.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 2 lat.<br>
            ${getCookiePolicyElement(
              'Polityka cookies Stripe',
              'https://stripe.com/en-pl/legal/cookies-policy',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'gpay',
      title: 'Google Pay',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Google Pay. They are saved during the payment process, for the purpose of its completion.<br>
            Third-party access: yes. Storage period: up to 1.5 years.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Google Ads Cookies List',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Google Pay. Zapisywane są w czasie przechodzenia przez proces płatności, na potrzeby jego realizacji.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 1,5 roku.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Lista ciastek używanych przez Google',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'apple_pay',
      title: 'Apple Pay',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Apple Pay. They are saved during the payment process, for the purpose of its completion.<br>
            ${getCookiePolicyElement(
              'Apple\'s use of cookies',
              'https://www.apple.com/legal/privacy/en-ww/cookies/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Apple Pay. Zapisywane są w czasie przechodzenia przez proces płatności, na potrzeby jego realizacji.<br>
            ${getCookiePolicyElement(
              'Korzystanie przez Apple z plików cookie',
              'https://www.apple.com/legal/privacy/pl/cookies/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'youtube',
      title: 'Youtube',
      translations: {
        en: {
          description: `
            Cookies used by YouTube enable the playback of videos embedded on the Pomagam.pl website. They are saved only when the user plays a video.<br>
            Third-party access: yes. Storage period: up to 1 year.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Google Ads Cookies List',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane przez YouTube w celu umożliwienia odtwarzania filmów zamieszczonych na stronie Pomagam.pl. Są one zapisywane tylko wtedy, gdy użytkownik uruchomi materiał wideo.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do roku.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Lista plików cookie używanych przez Google',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'vimeo',
      title: 'Vimeo',
      translations: {
        en: {
          description: `
            Cookies used by Vimeo enable the playback of videos embedded on the Pomagam.pl website. They are saved only when the user plays a video.<br>
            Third-party access: yes. Storage period: up to 2 years.<br>
            ${getCookiePolicyElement(
              'Vimeo Privacy Policy',
              'https://vimeo.com/legal/privacy/policy',
            )}<br>
            ${getCookiePolicyElement(
              'Vimeo Player Cookies',
              'https://help.vimeo.com/hc/en-us/articles/26080940921361-Vimeo-Player-Cookies',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane przez Vimeo w celu umożliwienia odtwarzania filmów zamieszczonych na stronie Pomagam.pl. Są one zapisywane tylko wtedy, gdy użytkownik uruchomi materiał wideo.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do dwóch lat.<br>
            ${getCookiePolicyElement(
              'Polityka prywatności Vimeo',
              'https://vimeo.com/legal/privacy/policy',
            )}<br>
            ${getCookiePolicyElement(
              'Lista plików cookie stosowanych przez Vimeo',
              'https://help.vimeo.com/hc/en-us/articles/26080940921361-Vimeo-Player-Cookies',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'google_tag_manager',
      title: 'Google Tag Manager',
      translations: {
        en: {
          description: `
            Cookies are used to initialize the Google Tag Manager tool for managing tags and scripts.<br>
            Third-party access: yes. Storage period: up to 1 minute.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Google Ads Cookies List',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w celu inicjalizacji narzędzia do zarządzania tagami oraz skryptami Google Tag Manager.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do minuty.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Lista plików cookie używanych przez Google',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'google_analytics',
      title: 'Google Analytics',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the Google Analytics service. Furthermore, on individual campaign pages, Google Analytics may also be run by the campaign organizer – exclusively within that campaign's page.<br>
            Third-party access: yes. Storage period: up to 2 years.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Google Ads Cookies List',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze Google Analytics. Ponadto na stronach poszczególnych zbiórek pliki Google Analytics mogą działać także po stronie organizatora danej zbiórki – wyłącznie w obrębie strony danej zbiórki.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 2 lat.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Lista plików cookie używanych przez Google',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        /^_ga(_.*)?/,
        '_gid'
      ],
    },
    {
      name: 'clarity',
      title: 'Microsoft Clarity',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the Microsoft Clarity service.<br>
            Third-party access: yes. Storage period: up to 1 day.<br>
            ${getCookiePolicyElement(
              'Microsoft Privacy Statement',
              'https://www.microsoft.com/en-us/privacy/privacystatement#maincookiessimilartechnologiesmodule',
            )}<br>
            ${getCookiePolicyElement(
              'Microsoft Clarity Cookies',
              'https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-cookies',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze Microsoft Clarity.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: jeden dzień.<br>
            ${getCookiePolicyElement(
              'Oświadczenie o ochronie prywatności w firmie Microsoft',
              'https://www.microsoft.com/pl-PL/privacy/privacystatement#maincookiessimilartechnologiesmodule',
            )}<br>
            ${getCookiePolicyElement(
              'Pliki cookie stosowane przez Microsoft Clarity',
              'https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-cookies',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        '_clck',
        '_clsk',
        '_cltk',
        'CLID',
        'ANONCHK',
        'MR',
        'MUID',
        'SM',
      ],
    },
    {
      name: 'log_rocket',
      title: 'LogRocket',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the LogRocket service.<br>
            Third-party access: yes. Storage period: up to 1 day.<br>
            ${getCookiePolicyElement(
              'LogRocket Privacy Policy',
              'https://logrocket.com/privacy',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze LogRocket.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: jeden dzień.<br>
            ${getCookiePolicyElement(
              'Polityka Prywatności LogRocket',
              'https://logrocket.com/privacy',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        /_lr_tabs_(-.*)?/,
        /_lr_hb_(-.*)?/,
      ],
    },
    {
      name: 'adc',
      translations: {
        en: {
          title: 'Pomagam.pl',
          description: `
            These cookies allow us to collect and process data based on our own solutions.
            <table class="cookie-table">
              <thead><tr><td>Name</td><td>Storage period</td><td>Description</td></tr></thead>
              <tbody>
                <tr><td>acd</td><td>30 days</td><td>Traffic source monitoring</td></tr>
              </tbody>
            </table>
            Third-party access: no.<br>
            ${getIternalCookiePolicyElement('Pomagam.pl Cookie Policy')}
          `,
        },
        pl: {
          title: 'Pomagam.pl',
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w oparciu o własne rozwiązania.
            <table class="cookie-table">
              <thead><tr><td>Nazwa</td><td>Okres przechowywania</td><td>Opis</td></tr></thead>
              <tbody>
                <tr><td>acd</td><td>30 dni</td><td>Monitorowanie źródeł ruchu</td></tr>
              </tbody>
            </table>
            Dostęp podmiotów trzecich: nie.<br>
            ${getIternalCookiePolicyElement('Polityka stosowania plików cookie Pomagam.pl')}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        'acd',
      ],
    },
    {
      name: 'meta_conversion_api',
      title: 'Meta (Facebook)',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the Meta advertising network. Furthermore, on individual campaign pages, Meta Pixel may be activated by the campaign organizer – exclusively within that campaign's page.<br>
            Third-party access: yes. Storage period: up to 90 days.<br>
            ${getCookiePolicyElement(
              'Meta Cookie Policy',
              'https://www.facebook.com/privacy/policies/cookies',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej Meta. Ponadto na stronach poszczególnych zbiórek może być uruchamiany Meta Pixel, umieszczony przez danego Organizatora – wyłącznie w obrębie strony danej zbiórki.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 90 dni.<br>
            ${getCookiePolicyElement(
              'Zasady stosowania plików cookie Meta',
              'https://www.facebook.com/privacy/policies/cookies?locale=pl_PL',
            )}
          `,
        },
      },
      cookies: [
        '_fbc',
        '_fbp',
        /^fbssls_(.*)?/,
      ],
      purposes: ['retargeting'],
    },
    {
      name: 'google_ads',
      title: 'Google',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the Google advertising network.<br>
            Third-party access: yes. Storage period: up to 13 months.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Google Ads Cookies List',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej Google.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 13 miesięcy.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/privacy/',
            )}<br>
            ${getCookiePolicyElement(
              'Lista ciastek używanych przez Google',
              'https://business.safety.google/adscookies/',
            )}
          `,
        },
      },
      cookies: [
        /^_gcl_(.*)?/,
      ],
      purposes: ['retargeting'],
    },
    {
      name: 'tiktok',
      title: 'TikTok',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the TikTok advertising network.<br>
            Third-party access: yes. Storage period: up to 13 months.<br>
            ${getCookiePolicyElement(
              'TikTok Cookie Policy',
              'https://ads.tiktok.com/help/article/using-cookies-with-tiktok-pixel',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej TikTok.<br>
            Dostęp podmiotów trzecich: tak. Okres przechowywania: do 13 miesięcy.<br>
            ${getCookiePolicyElement(
              'Polityka plików cookie TikTok',
              'https://ads.tiktok.com/help/article/using-cookies-with-tiktok-pixel?lang=pl-PL',
            )}<br>
            ${getCookiePolicyElement(
              'Lista plików cookies używanych przez TikTok',
              'https://ads.tiktok.com/help/article/using-cookies-with-tiktok-pixel',
            )}
          `,
        },
      },
      cookies: [
        '_ttp',
        '_tt_enable_cookie',
        /^ttcsid_(.*)?/,
        '_pangle',
        'ttcsid',
        'tt_sessionId',
        'tt_appInfo',
      ],
      purposes: ['retargeting'],
    },
    {
      name: 'pomagam_retargeting',
      title: 'Pomagam.pl',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages based on our own solutions.
             <table class="cookie-table">
              <thead><tr><td>Name</td><td>Storage period</td><td>Description</td></tr></thead>
              <tbody>
                <tr><td>TaxCampaign2025Visited</td><td>180 days</td><td>Counting unique conversions</td></tr>
              </tbody>
            </table>
            Third-party access: no.<br>
            ${getIternalCookiePolicyElement('Pomagam.pl Cookie Policy')}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam prezentować trafniejsze komunikaty w oparciu o własne rozwiązania.
            <table class="cookie-table">
              <thead><tr><td>Nazwa</td><td>Okres przechowywania</td><td>Opis</td></tr></thead>
              <tbody>
                <tr><td>TaxCampaign2025Visited</td><td>180 dni</td><td>Zliczanie unikalnych konwersji</td></tr>
              </tbody>
            </table>
            Dostęp podmiotów trzecich: nie.<br>
            ${getIternalCookiePolicyElement('Polityka Prywatności Pomagam.pl')}
          `,
        },
      },
      cookies: ['TaxCampaign2025Visited'],
      purposes: ['retargeting'],
    },
  ],

  /*
  You can define an optional callback function that will be called each time the
  consent state for any given service changes. The consent value will be passed as
  the first parameter to the function (true=consented). The `service` config will
  be passed as the second parameter.
  */
  callback: function(consent, service) {
    // DEVS: This is a workaround for default Klaro behavior, which calls onDecline hooks on the initial load,
    // when cookies preferences are not set yet.
    // Google Consent Mode requires that only a default consent is sent until user decides to accept or decline consent.
    // Klaro calls `onDecline` hooks on initial load (when no consents are chosen yet), so a custom handling is required.
    const name = service.name;
    if (name in serviceHooks) {
      // Check previously granted Klaro consent for the service (stored in cookies).
      const consentState = hasGrantedKlaroConsent(name);
      if (consentState === undefined) {
        // Consent not set yet, so hooks are not called.
        return;
      }
      consentState ? serviceHooks[name].onAccept() : serviceHooks[name].onDecline();
    }
  },
};

// Google Consent Mode V2 consent types
const ANALYTICS_STORAGE = 'analytics_storage';
const AD_STORAGE = 'ad_storage';
const AD_USER_DATA = 'ad_user_data';
const AD_PERSONALIZATION = 'ad_personalization';

function updateGoogleConsents(isGranted, consentTypes = []) {
  // Updates Google Consent Mode V2 consents to a single value for all provided consent types.
  // The value is 'granted' or 'denied' based on the isGranted parameter.
  // Requires GoogleTagManager to be loaded and initialized as `gtag`.
  const value = isGranted ? 'granted' : 'denied';
  const consents = consentTypes.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {});
  // Update GTM consents.
  gtag('consent', 'update', consents);
}

// Custom service hooks for consents that require special onAccept/onDecline handling.
const serviceHooks = {
  google_analytics: {
    onAccept: () => {
      if (hasGrantedKlaroConsent('google_ads')) {
        // GoogleAnalytics permission accepted hook is called BEFORE GoogleAds, so let's skip sending the event for now.
        return;
      } else {
        // GoogleAds is not granted, so consent update for GoogleAnalytics can be safely sent.
        updateGoogleConsents(true, [ANALYTICS_STORAGE]);
      }
    },
    onDecline: () => {
      // Google Consent Mode V2 update: analytics storage
      updateGoogleConsents(false, [ANALYTICS_STORAGE]);
    },
  },
  google_ads: {
    onAccept: () => {
      // Google Consent Mode V2 update
      // DEVS WARNING: This code leverages permission check order to ensure that GoogleAnalytics x GoogleAds combo
      // is handled accordingly to specs: only one gtag consent update is sent.

      // Check if GoogleAnalytics had been granted, but sending a consent update has been postponed.
      // In such case, both services require one single content update call.
      // Otherwise, only GoogleAds consents are being updated.
      let consents = [AD_STORAGE, AD_USER_DATA, AD_PERSONALIZATION];
      if (hasGrantedKlaroConsent('google_analytics')) {
        consents.push(ANALYTICS_STORAGE);
      }
      updateGoogleConsents(true, consents);
    },
    onDecline: () => {
      // Google Consent Mode V2 update: ad storage and personalization
      // Decline hooks are called separately for each service.
      updateGoogleConsents(false, [AD_STORAGE, AD_USER_DATA, AD_PERSONALIZATION]);
    },
  },
  log_rocket: {
    onAccept: () => {
      const LOG_ROCKET_SCRIPT_URL = 'https://cdn.lgrckt-in.com/LogRocket.min.js';
      if (!window.logRocketData) {
        return;
      }

      function logRocketInit(data) {
        // Start LogRocket session with optional user identification
        window.LogRocket.init(data.id, { release: data.release });
        if (data.user) {
          window.LogRocket.identify(data.user.id, {
            // Disabled for privacy reasons until it's discussed and decided.
            // name: data.user.name,
            // email: data.user.email,
            type: data.user.type,
          });
        }
      }
      // Script is loaded asynchronously to avoid blocking the page load.
      // LogRocket.init and LogRocket.identify are called after the script is loaded to ensure LogRocket is available.
      loadScript(LOG_ROCKET_SCRIPT_URL, function() {
        // try/catch is used to handle known 'Java is gone' error in Facebook in-app browser.
        try {
          const data = window.logRocketData || {};
          logRocketInit(data);
        } catch (e) {
          console.warn('Error initializing LogRocket:', e);
        }
      });
    },
    onDecline: () => {},
  },
  adc: {
    onAccept: () => {
      const ADC_PAGE_CLASS = 'opt-acd'; // Class for pages where ADC is enabled
      // Load only on dedicated pages
      if ($('#page').hasClass(ADC_PAGE_CLASS)) {
        AdCampaignDataToCookie(true);
      }
    },
    onDecline: () => {},
  },
  tiktok: {
    onAccept: () => dataLayer.push({ event: 'klaro-tiktok-accepted' }),
    onDecline: () => dataLayer.push({ event: 'klaro-tiktok-declined' }),
  },
  clarity: {
    onAccept: () => dataLayer.push({ event: 'klaro-clarity-accepted' }),
    onDecline: () => dataLayer.push({ event: 'klaro-clarity-declined' }),
  },
};

function hasGrantedKlaroConsent(value) {
  // Checks if klaro consent is granted for the given value.
  const cookies_consent = JSON.parse(getCookie(COOKIE_NAME) || '{}');
  return cookies_consent[value];
}

// Set the language for Klaro based on the Django language settings in js/jsi18n/<lang>/djangojs.js
// Language presence in that file is a custom override of the output of Django's 'makemessages -all -d djangojs' command.
// See fabfile.py for more details.
klaroConfig.lang = window.django.lang in klaroConfig.translations ? window.django.lang : 'en';

export function setupKlaro() {
  // Initialize Klaro with the configuration
  klaro.setup(klaroConfig);
}

export function showKlaro() {
  klaro.show(klaroConfig);
}

$(document).ready(() => {
  // Load Klaro consent manager
  setupKlaro();
  window.showKlaro = showKlaro;
});
